import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { LocalStorageKeys } from 'src/app/enum/local-storage-keys.enum';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { PoolManagerService } from 'src/app/services/pool-manager/pool-manager.service';

import {
  GetPaginatedFreelancePoolServicesRequest,
  PoolService,
  PoolServiceItem,
  PoolServiceNotStarted,
} from './interface/pool-service.interface';
import { ModalDuplicateServiceComponent } from '../modal-duplicate-service/modal-duplicate-service.component';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { ModalMode } from '../modal-duplicate-service/model/modal-duplicate-service.model';

type PoolManagementTab = 'pool-management' | 'historical';

@Component({
  selector: 'app-pool-management',
  templateUrl: './pool-management.component.html',
  styleUrls: ['./pool-management.component.css'],
})
export class PoolManagementComponent implements OnInit {
  activeLink: PoolManagementTab = 'pool-management';

  freelancePoolServicesCount = 0;
  freelancePoolServicesNotStartedCount = 0;

  countOfServiceNotStarted = 0;

  pendingServices: PoolServiceItem[] = [];
  historicalServices: PoolServiceItem[] = [];
  historicalFilters?: GetPaginatedFreelancePoolServicesRequest;
  historicalTotalItems = 0;
  historicalPageIndex = 0;
  readonly historicalPageSize = 25;

  isLoadingPending = false;
  isLoadingHistorical = false;

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly router: Router,
    private readonly poolService: PoolManagerService,
    private readonly translate: TranslateService,
    private readonly offcanvas: NgbOffcanvas // ⬅️ AÑADIDO
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadPendingServices();
  }

  async setActiveLink(link: PoolManagementTab): Promise<void> {
    this.countOfServiceNotStarted = 0;
    this.activeLink = link;

    if (link === 'historical' && this.historicalServices.length === 0) {
      const filters = this.getEffectiveHistoricalFilters();
      await this.loadHistoricalServices(false, filters);
    }
  }

  getCountServicesNotStarted(count: number): void {
    this.countOfServiceNotStarted = count;
  }

  redirect(route: string): void {
    this.localStorageService.set(LocalStorageKeys.OPENMODAL, '1');
    this.router.navigate([route]);
  }

  private async loadPendingServices(force = false): Promise<void> {
    if (!force && this.pendingServices.length > 0) {
      this.freelancePoolServicesNotStartedCount = this.pendingServices.length;
      return;
    }

    this.isLoadingPending = true;
    try {
      const resp: PoolServiceNotStarted = await firstValueFrom(
        this.poolService.getNotStartedPools()
      );

      this.pendingServices = Array.isArray(resp?.pendingServices)
        ? resp.pendingServices
        : [];
      this.freelancePoolServicesNotStartedCount = this.pendingServices.length;
    } catch (error) {
      this.pendingServices = [];
      this.freelancePoolServicesNotStartedCount = 0;
    } finally {
      this.isLoadingPending = false;
    }
  }

  openModalAddService(): void {
    const ref = this.offcanvas.open(ModalDuplicateServiceComponent, {
      backdrop: 'static',
      position: 'end',
    });

    (ref.componentInstance as ModalDuplicateServiceComponent).type_info =
      ModalMode.Add;

    if ('finished' in ref.componentInstance) {
      (ref.componentInstance as any).finished
        .pipe(take(1))
        .subscribe(async (wasSuccessful: boolean) => {
          if (!wasSuccessful) return;

          await Promise.all([
            this.loadPendingServices(true),
            this.loadHistoricalServices(true, this.getEffectiveHistoricalFilters()),
          ]);
        });
    }
  }

  private async loadHistoricalServices(
    force = false,
    filters?: GetPaginatedFreelancePoolServicesRequest
  ): Promise<void> {
    if (!force && this.historicalServices.length > 0) {
      this.freelancePoolServicesCount = this.historicalServices.length;
      return;
    }

    this.isLoadingHistorical = true;
    try {
      const resp: PoolService = await firstValueFrom(
        this.poolService.getPaginatedFreelancePoolServices(filters)
      );
      this.historicalServices = Array.isArray(resp?.services)
        ? resp.services
        : [];

      this.historicalTotalItems = Number(
        resp?.totalItems ?? this.historicalServices.length
      );
      this.freelancePoolServicesCount = this.historicalTotalItems;
    } catch (error) {
      this.historicalServices = [];
      this.historicalTotalItems = 0;
      this.freelancePoolServicesCount = 0;
    } finally {
      this.isLoadingHistorical = false;
    }
  }

  async refreshPendingServices(): Promise<void> {
    await this.loadPendingServices(true);
  }

  async refreshHistoricalServices(): Promise<void> {
    await this.loadHistoricalServices(true, this.getEffectiveHistoricalFilters());
  }

  async onHistoricalFiltersChange(
    filters: GetPaginatedFreelancePoolServicesRequest
  ): Promise<void> {
    this.historicalFilters = filters;
    this.historicalPageIndex = Math.max(0, (filters.pageNumber ?? 1) - 1);
    await this.loadHistoricalServices(true, filters);
  }

  private getEffectiveHistoricalFilters(): GetPaginatedFreelancePoolServicesRequest {
    if (this.historicalFilters) {
      return this.historicalFilters;
    }

    const today = new Date();
    const lastFifteenDays = new Date();
    lastFifteenDays.setDate(today.getDate() - 15);

    this.historicalFilters = {
      fromDate: this.formatDateOnly(lastFifteenDays),
      toDate: this.formatDateOnly(today),
      pageNumber: this.historicalPageIndex + 1,
      pageSize: this.historicalPageSize,
      zoneGuids: [],
      productsName: [],
      clientsGuid: [],
      serviceStatus: [],
      driverNames: [],
      serviceFilter: '',
    };

    return this.historicalFilters;
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
